// @flow
import React from 'react';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { themr } from 'react-css-themr';
import { Helmet } from 'react-helmet';
import { Route, Switch, Redirect } from 'react-router-dom';
import { translate, type TFunction } from 'react-i18next';

import FpPlanViewActions from 'common/redux/actions/FpPlanViewActions';
import {
    RouteNotFound,
    withRouter,
    type Location
} from 'common/components/core/router';
import { personnelSelector } from 'common/redux/selectors/sessionSelector';

import type { List, Map } from 'immutable';
import type { FpPjPlan } from 'common/models/internal/FpPjPlan';
import type { RouterHistory, Match } from 'react-router-dom';

import { NavTabs } from 'common/components/core/tabs';
import queryString from 'query-string';

import { teamsSelector } from 'common/redux/selectors/teamSelector';
import {
    ClientSelectors,
    getClientDimensionsMapping
} from 'common/redux/selectors/clientSelector';
import { clientSelector } from 'common/redux/selectors/queryParamsSelectors';
import { getSelectedBUId } from 'common/redux/selectors/appPreferences';

import ToastActions from 'common/redux/actions/toast';

import { Box, Stack, Text, Columns, Flex } from '@locus-taxy/neo-ui';
import PjpExportHandler from 'common/entity/pjpMaster/PjpExportHandler';
import { COLUMNS } from 'common/entity/pjpMaster/PjpMasterContainer';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';

import theme from './FpPlanDetailsContainer.scss';

import { Team } from 'common/models/internal/Team';
import type { SalesCategoryModelType } from 'common/models/flow/SalesCategory';
import { ApiError } from 'common/modules/api';

import { withUnitConverter } from 'common/containers/intl';
import SectionWrap from 'common/components/common/SectionWrap';
import TrackedPjpInsights from 'common/entity/pjpMaster/PjpInsightsContainer';
import FpDetailsHeader from './FpDetailsHeader';
import { headerWithLogoutAndBUFactory } from 'common/components/core/app_bar/HeaderWithLogoutAndBU';
import { createAppHeader } from 'common/containers/AppHeaderContainer';
import FpPlanViewContainer from './FpPlanViewContainer';
import FpPlanData from './FpPlanData';
import OverlayDialog from 'common/components/core/dialog/OverlayDialog';
import MasterExportContainer from 'common/entity/generic/MasterExportContainer';
import { type FpVisitAddress } from 'common/models/internal/FpVisitAddress';
import { type FpVisitDays } from 'common/models/internal/FpOutletVisit';
import { type FpSalesbeatPlan } from 'common/models/internal/FpSalesbeatPlan';
import UnitConversionHelper from 'common/helpers/UnitConversionHelper';

import {
    getFpPlan,
    isPlanLoading,
    getVisitIdMap,
    getSalesbeatPlan,
    isSalesbeatPlanLoading,
    getOutletSalesCategories
} from 'common/redux/selectors/fpPlanViewSelectors';
import Loader from 'common/components/core/loader/Loader';
import PlanEditsDialog from './PlanEditsDialog';
import PlanEditProvider, { PlanEditContext } from './planEdits/PlanEditContext';
import { EditToolbar } from './planEdits//EditToolbar';

const AppHeader = headerWithLogoutAndBUFactory({});

const FpPlanViewAppHeader = createAppHeader({
    HeaderComponent: AppHeader
});

export const FPMetricTabs = Object.freeze({
    insights: 'insights',
    planView: 'planView',
    planData: 'planData'
});

const FieldProDetailsTabs = Object.freeze([
    {
        title: 'fpPlanView.insights',
        tabId: FPMetricTabs.insights,
        path: `${FPMetricTabs.insights}`
    },
    {
        title: 'fpPlanView.planView',
        tabId: FPMetricTabs.planView,
        path: `${FPMetricTabs.planView}`
    },
    {
        title: 'fpPlanView.planData',
        tabId: FPMetricTabs.planData,
        path: `${FPMetricTabs.planData}`
    }
]);

type EditVisitType = Array<{
    visitId: string,
    targetBeatId?: string,
    originalBeatId?: string
}>;

type State = {
    activeTabId: ?string,
    showEditsDialog: boolean,
    showExportDialog: boolean,
    showExportLoader: boolean,
    plan: FpPjPlan,
    exportErrorMessage: ?ApiError
};

export const FpContainers = Object.freeze({
    Allocated: 'allocated',
    Unallocated: 'unallocated',
    PlanData: 'planData',
    Insights: 'insights'
});

export type VisitIdOutletType = {
    address: FpVisitAddress,
    category: string,
    id: string,
    possibleDays: Array<FpVisitDays>,
    revenue: {
        amount: ?number,
        currency: ?string,
        symbol: ?string
    },
    sourceOutletId: string,
    visitType: string,
    zoneId: ?string
};

type Props = {
    showToastAction: () => void,
    updateToastAction: () => void,
    hideToastAction: () => void,
    fetchPjFpPlan: (clientId: string, pjpId: string) => void,
    fetchOutletSalesCategories: (Object, Object) => void,
    editVisit: (
        {
            clientId: string,
            planId: string,
            pjpId: string
        },
        edits: EditVisitType,
        dryRun?: boolean
    ) => Promise<any>,
    outletSalesCategories: Array<SalesCategoryModelType>,
    freezePlan: Function, //@fix
    teams: List<Team>,
    clientId: string,
    pjpId: string,
    plan: ?FpPjPlan,
    salesbeatPlan: ?FpSalesbeatPlan,
    visitIdMap: Map<string, VisitIdOutletType>,
    planLoading: boolean,
    salesbeatPlanLoading: boolean,
    defaultEmail: ?string,
    businessUnitId: ?string,
    location: Location,
    history: RouterHistory,
    match: Match,
    t: TFunction,
    fetchData: () => void
};

class FpPlanDetailsContainer extends React.Component<Props, State> {
    constructor(props) {
        super(props);

        this.state = {
            activeTabId: null,
            showEditsDialog: false,
            showExportDialog: false,
            showExportLoader: false,
            plan: null,
            exportErrorMessage: null,
            metricsBeforeEdit: null
        };
    }

    componentDidMount() {
        const { clientId, location } = this.props;
        if (clientId) {
            const { pathname } = location;
            this.setState(
                {
                    activeTabId: this.getSelectedView(pathname)
                },
                () => {
                    this.props.fetchData();
                }
            );
        }
    }

    componentDidUpdate(prevProps: Props) {
        const { clientId, pjpId, location, plan } = this.props;
        const { edits } = this.context;
        let metricsBeforeEdit;
        const {
            location: prevLocation,
            pjpId: prevPjpId,
            clientId: prevClientId,
            plan: prevPlan
        } = prevProps;
        if (clientId !== prevClientId || pjpId !== prevPjpId) {
            this.props.fetchData();
        }
        if (!edits && plan && plan !== prevPlan) {
            metricsBeforeEdit = this.getMetricsBeforeEdits(plan);
            this.setState({
                metricsBeforeEdit
            });
        }
        if (plan && plan !== prevPlan) {
            this.setState({
                plan
            });
        }
        const { pathname } = location;
        const { pathname: prevPathname } = prevLocation;
        if (pathname !== prevPathname) {
            this.setState({
                activeTabId: this.getSelectedView(pathname),
                showEditsDialog: false,
                showExportDialog: false
            });
        }
    }

    componentWillUnmount() {
        const { clearFpData } = this.props;
        clearFpData();
    }

    static contextType = PlanEditContext;

    getMetricsBeforeEdits = plan => {
        const {
            availableOutletCount,
            availableSalesmenCount
        } = plan.pjPlanInsights.planDetails.currentPlan;
        const totalOutlets = plan.outletsInfo.outletIds.length;
        const totalRiders = plan.ridersInfo.riderIds.length;
        if (plan && plan.salesbeatPlan) {
            const {
                assignedVisitCount,
                beatCount,
                totalTravelDistance,
                totalTravelDuration,
                totalVolume
            } = plan.salesbeatPlan.summary;
            const metricsBeforeEdit = {
                availableOutletCount,
                availableSalesmenCount,
                totalOutlets,
                totalRiders,
                assignedVisitCount,
                beatCount,
                totalTravelDistance,
                totalTravelDuration,
                totalVolume
            };
            return metricsBeforeEdit;
        }
        return null;
    };
    getSelectedView = (pathname: string) => {
        const path = pathname.split('/');
        if (path.includes(FPMetricTabs.insights)) {
            return FPMetricTabs.insights;
        } else if (path.includes(FPMetricTabs.planView)) {
            return FPMetricTabs.planView;
        } else if (path.includes(FPMetricTabs.planData)) {
            return FPMetricTabs.planData;
        } else {
            return FPMetricTabs.insights;
        }
    };

    updateLocation = ({
        pathname,
        extraQueryParams = {}
    }: {
        pathname?: string,
        extraQueryParams: any
    }) => {
        const {
            location: { query },
            history
        } = this.props;
        const queryParams = {
            ...query,
            ...extraQueryParams
        };
        history.push({
            pathname,
            search: queryString.stringify(queryParams)
        });
    };

    reloadData = () => {
        this.props.fetchData();
    };

    handleTabChange = (index: number) => {
        const { clientId, pjpId } = this.props;
        const { path, tabId } = FieldProDetailsTabs[index];
        const pathname = `/client/${clientId}/pjp/${pjpId}/fpPlan/${path}`;
        this.setState(
            {
                activeTabId: tabId
            },
            () => {
                this.updateLocation({ pathname, extraQueryParams: {} });
            }
        );
    };

    renderPjpInsights = () => {
        const { t, ...rest } = this.props;
        return (
            <SectionWrap>
                <TrackedPjpInsights theme={theme} t={t} {...rest} />
            </SectionWrap>
        );
    };

    renderPlanDetails = () => {
        const {
            t,
            clientId,
            pjpId,
            plan,
            visitIdMap,
            salesbeatPlan,
            UnitConverter
        } = this.props;
        if (!salesbeatPlan && UnitConverter) {
            return (
                <SectionWrap>
                    <Box p={2}>
                        <Columns width="fluid" align="center">
                            <Text fontSize={3} fontWeight={600}>
                                {t('fpPlanView.noSalesbeatPlan')}
                            </Text>
                        </Columns>
                    </Box>
                </SectionWrap>
            );
        }
        return (
            <FpPlanData
                UnitConverter={UnitConverter}
                visitIdMap={visitIdMap}
                pjpId={pjpId}
                clientId={clientId}
                plan={plan}
                salesbeatPlan={salesbeatPlan}
                theme={theme}
                t={t}
            />
        );
    };

    renderPlanViewContainer = () => {
        const {
            t,
            clientId,
            plan,
            pjpId,
            visitIdMap,
            outletSalesCategories,
            fetchPjFpPlan,
            salesbeatPlan,
            UnitConverter
        } = this.props;
        if (!salesbeatPlan) {
            return (
                <SectionWrap>
                    <Box p={2}>
                        <Columns width="fluid" align="center">
                            <Text fontSize={3} fontWeight={600}>
                                {t('fpPlanView.noSalesbeatPlan')}
                            </Text>
                        </Columns>
                    </Box>
                </SectionWrap>
            );
        }
        return (
            <SectionWrap>
                <FpPlanViewContainer
                    t={t}
                    UnitConverter={UnitConverter}
                    visitIdMap={visitIdMap}
                    theme={theme}
                    plan={plan}
                    salesbeatPlan={salesbeatPlan}
                    clientId={clientId}
                    pjpId={pjpId}
                    outletSalesCategories={outletSalesCategories}
                    reloadData={this.reloadData}
                    fetchPjFpPlan={fetchPjFpPlan}
                />
            </SectionWrap>
        );
    };

    buildExportRequestParams = () => {
        const { businessUnitId, defaultEmail } = this.props;
        const accessors = new Set();
        let columnAccessors = [];
        COLUMNS.forEach(column => {
            if (typeof column.accessor === 'string') {
                accessors.add(column.accessor);
            } else {
                const value = column.id;
                accessors.add(value);
            }
        });
        columnAccessors = Array.from(accessors);
        // Default Master Filter
        const defaultActiveFilter = new FilterListBuilder()
            .add(
                new FilterBuilder()
                    .withName('status')
                    .withOperation(FilterOperation.Equals)
                    .withValue('ACTIVE')
            )
            .build();
        const requestParams = {
            businessUnitId,
            selectedColumns: columnAccessors,
            emailAddresses: [{ emailAddress: defaultEmail }],
            entityName: 'PJP_EXPORT',
            filters: [...defaultActiveFilter],
            selectedIds: []
        };
        return requestParams;
    };

    handleEntityExportAccept = () => {
        const {
            showToastAction,
            updateToastAction,
            hideToastAction,
            t
        } = this.props;
        const { id, clientId } = this.state.plan;
        const EXPORT_HANDLER = new PjpExportHandler();
        this.setState({ showExportLoader: true });
        let options = { planId: id };
        options = {
            entityMasterName: t(
                `entityManagement:entityExport.master.PJP_EXPORT`
            ),
            ...options
        };
        const requestParams = this.buildExportRequestParams();
        EXPORT_HANDLER.initialiseAndMakeExportRequest(
            requestParams,
            clientId,
            showToastAction,
            updateToastAction,
            hideToastAction,
            options
        )
            .then(() => {
                this.onExportDismiss();
            })
            .catch(error => {
                this.setState({
                    exportErrorMessage:
                        error instanceof ApiError ? error.message : null,
                    showExportLoader: false
                });
            });
    };

    getFilter = () => {
        return new FilterListBuilder().addAll([]);
    };

    onExportDismiss = () => {
        this.setState({
            showExportDialog: false,
            showExportLoader: false
        });
    };

    showEditsDialog = () => {
        this.setState({
            showEditsDialog: true
        });
    };

    closeEditsDialog = () => {
        this.setState({
            showEditsDialog: false
        });
    };

    showExportDialog = () => {
        this.setState({
            showExportDialog: true
        });
    };

    closeExportDialog = () => {
        this.setState({
            showExportDialog: false
        });
    };

    freezePlan = (): Promise<> => {
        const { freezePlan } = this.props;
        return freezePlan.then(() => {
            this.reloadData();
        });
    };

    render() {
        const {
            match,
            t,
            plan,
            freezePlan,
            clientId,
            pjpId,
            planLoading,
            outletSalesCategories,
            salesbeatPlanLoading,
            salesbeatPlan,
            UnitConverter
        } = this.props;
        const {
            showEditsDialog,
            showExportDialog,
            showExportLoader,
            metricsBeforeEdit
        } = this.state;
        if (
            plan &&
            !planLoading &&
            outletSalesCategories &&
            !salesbeatPlanLoading
        ) {
            return (
                <Box height="fluid" className={theme.fieldProDetailsContainer}>
                    <Stack height="fluid">
                        <Box>
                            <FpDetailsHeader
                                t={t}
                                plan={plan}
                                pjpId={pjpId}
                                salesbeatPlan={salesbeatPlan}
                                clientId={clientId}
                                freezePlan={freezePlan}
                                reloadData={this.reloadData}
                                showEditsDialog={this.showEditsDialog}
                                showExportDialog={this.showExportDialog}
                                metricsBeforeEdit={metricsBeforeEdit}
                                UnitConverter={UnitConverter}
                            />
                        </Box>
                        {showEditsDialog ? (
                            <Box className={theme.overlays}>
                                <OverlayDialog
                                    active={showEditsDialog}
                                    theme={theme}
                                    dialogClassName={theme.planEditsDialog}
                                >
                                    <PlanEditsDialog
                                        t={t}
                                        plan={plan}
                                        closeEditsDialog={this.closeEditsDialog}
                                        salesbeatPlan={salesbeatPlan}
                                        theme={theme}
                                    />
                                </OverlayDialog>
                            </Box>
                        ) : null}
                        {showExportDialog ? (
                            <MasterExportContainer
                                hideSubtitles
                                hideExportOptions
                                defaultEmail={this.props.defaultEmail}
                                showLoader={showExportLoader}
                                onAccept={this.handleEntityExportAccept}
                                appliedFilters={this.getFilter()}
                                onDismiss={this.onExportDismiss}
                                entityNameId="PJP_EXPORT"
                                columns={COLUMNS}
                            />
                        ) : null}
                        <Box pr={2} boxShadow="normal">
                            <Flex
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <NavTabs
                                    t={t}
                                    theme={theme}
                                    tabs={FieldProDetailsTabs}
                                    activeTabId={this.state.activeTabId}
                                    handleTabChange={this.handleTabChange}
                                />
                                <EditToolbar />
                            </Flex>
                        </Box>

                        <Box className={theme.fieldProDetailsViewSection}>
                            <Switch>
                                <Route
                                    path={`${match.path}/insights`}
                                    component={this.renderPjpInsights}
                                />
                                <Route
                                    path={`${match.path}/planView`}
                                    component={this.renderPlanViewContainer}
                                />
                                <Route
                                    path={`${match.path}/planData`}
                                    component={this.renderPlanDetails}
                                />
                                <Redirect
                                    exact
                                    from={match.path}
                                    to={`${match.path}/insights`}
                                />
                                <RouteNotFound />
                            </Switch>
                        </Box>
                    </Stack>
                </Box>
            );
        }
        return <Loader active />;
    }
}

const mapStateToProps = (state, ownProps) => {
    const {
        match: { params }
    } = ownProps;
    const { pjpId } = params;
    const teams = teamsSelector(state, ownProps);
    const outletSalesCategories = getOutletSalesCategories(state);
    const defaultCountryCenter = ClientSelectors.getCountryCenterFromLocaleSettings(
        state,
        ownProps
    );
    const personnel = personnelSelector(state);
    const clientId = clientSelector(state, ownProps);
    const planLoading = isPlanLoading(state);
    const businessUnitId = getSelectedBUId(state, ownProps);
    let visitIdMap;
    const salesbeatPlanLoading = isSalesbeatPlanLoading(state);
    const plan = getFpPlan(state);
    const salesbeatPlan = getSalesbeatPlan(state);
    if (!planLoading && plan) {
        visitIdMap = getVisitIdMap(state);
    }
    const dimensionsMapping = getClientDimensionsMapping(state, ownProps);
    const UnitConverter = new UnitConversionHelper({ dimensionsMapping });

    return {
        ...ownProps,
        defaultCountryCenter,
        teams,
        defaultEmail: personnel?.email,
        clientId,
        pjpId,
        plan,
        salesbeatPlan,
        visitIdMap,
        planLoading,
        outletSalesCategories,
        businessUnitId,
        salesbeatPlanLoading,
        UnitConverter
    };
};

const withRedux = connect(mapStateToProps, {
    showToastAction: ToastActions.showToast,
    updateToastAction: ToastActions.updateToast,
    hideToastAction: ToastActions.hideToast,
    fetchPjFpPlan: FpPlanViewActions.fetchFpPjPlan,
    fetchSalesbeatPlan: FpPlanViewActions.fetchPjSalesBeatPlan,
    editVisit: FpPlanViewActions.editVisit,
    fetchOutletSalesCategories: FpPlanViewActions.fetchOutletSalesCategories,
    freezePlan: FpPlanViewActions.freezePlan,
    clearFpData: FpPlanViewActions.clearFpPlanViewData
});

const FpPlanDetailsContainerWithEditImpacts = props => {
    const fetchData = React.useCallback(() => {
        const {
            fetchPjFpPlan,
            pjpId,
            clientId,
            fetchOutletSalesCategories
        } = props;
        fetchPjFpPlan(clientId, pjpId);
        fetchOutletSalesCategories(clientId, pjpId);
    }, [props]);
    return (
        <PlanEditProvider
            clientId={props.clientId}
            plan={props.plan}
            pjpId={props.pjpId}
            saveCallback={fetchData}
            editVisit={props.editVisit}
        >
            <FpPlanDetailsContainer {...props} fetchData={fetchData} />
        </PlanEditProvider>
    );
};

const ConnectedFpPlanDetailsContainer = compose(
    themr('null', theme),
    withUnitConverter,
    withRouter,
    withRedux,
    translate('entityManagement')
)(FpPlanDetailsContainerWithEditImpacts);

const ConnectedFpPlanViewHeader = compose(
    withRouter,
    connect(mapStateToProps)
)(FpPlanViewAppHeader);

const FieldProDetailsSection = props => {
    return (
        <SectionWrap>
            <Helmet defer={false}>
                <title>
                    {props.t('entityManagement:fpPlanView.fieldProPlanView')}
                </title>
            </Helmet>

            <ConnectedFpPlanViewHeader {...props} />
            <ConnectedFpPlanDetailsContainer {...props} />
        </SectionWrap>
    );
};

export default FieldProDetailsSection;
